import { apiClient } from '../api-client'

export type FileCategory = 'project-documents' | 'milestones' | 'tasks' | 'bugs' | 'tickets' | 'others'

export interface PresignUploadRequest {
  project_id: string
  category: FileCategory
  filename: string
  content_type: string
  entity_type?: string
  entity_id?: string
  expires_in?: number
  metadata?: Record<string, any>
}

export interface PresignUploadResponse {
  upload_url: string
  key: string
  bucket: string
  object_url: string
  expires_in: number
}

export interface CompleteUploadRequest {
  project_id: string
  category: FileCategory
  key: string
  filename: string
  original_filename: string
  file_size: number
  mime_type: string
  entity_type: string
  entity_id: string
  metadata?: Record<string, any>
}

export interface BackendUploadRequest {
  project_id: string
  category: FileCategory
  entity_type: string
  entity_id: string
  original_filename?: string
  metadata?: Record<string, any>
}

export interface ReplaceAttachmentFileRequest {
  attachment_id: string
  project_id: string
  category: FileCategory
  original_filename?: string
  metadata?: Record<string, any>
}

export interface StoredAttachment {
  title?: string
  id: string
  entity_type: string
  entity_id: string
  filename: string
  original_filename: string
  file_path: string
  file_size: number
  mime_type: string
  created_at: string
  updated_at?: string
  metadata?: Record<string, any>
}

export interface UpdateAttachmentRequest {
  title?: string
  key?: string
  filename?: string
  original_filename?: string
  file_size?: number
  mime_type?: string
  metadata?: Record<string, any>
}

export interface PresignDownloadResponse {
  download_url: string
  expires_in: number
}

export const fileStorageApi = {
  presignUpload: (payload: PresignUploadRequest): Promise<PresignUploadResponse> =>
    apiClient.post('/files/presign-upload', payload),

  completeUpload: (payload: CompleteUploadRequest): Promise<{ attachment_id: string; key: string; object_url: string }> =>
    apiClient.post('/files/complete-upload', payload),

  uploadViaBackend: async (
    payload: BackendUploadRequest,
    file: File
  ): Promise<{ attachment_id: string; key: string; object_url: string }> => {
    const formData = new FormData()
    formData.append('project_id', payload.project_id)
    formData.append('category', payload.category)
    formData.append('entity_type', payload.entity_type)
    formData.append('entity_id', payload.entity_id)
    formData.append('original_filename', payload.original_filename || file.name)
    formData.append('file', file)
    if (payload.metadata) {
      formData.append('metadata', JSON.stringify(payload.metadata))
    }

    return apiClient.post('/files/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  uploadEntityAttachment: (
    payload: BackendUploadRequest,
    file: File
  ): Promise<{ attachment_id: string; key: string; object_url: string }> =>
    fileStorageApi.uploadViaBackend(payload, file),

  replaceAttachmentFile: async (
    payload: ReplaceAttachmentFileRequest,
    file: File
  ): Promise<StoredAttachment> => {
    const formData = new FormData()
    formData.append('project_id', payload.project_id)
    formData.append('category', payload.category)
    formData.append('original_filename', payload.original_filename || file.name)
    formData.append('file', file)
    if (payload.metadata) {
      formData.append('metadata', JSON.stringify(payload.metadata))
    }

    return apiClient.post(`/files/attachments/${payload.attachment_id}/replace`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },

  listAttachments: (entityType: string, entityId: string): Promise<StoredAttachment[]> =>
    apiClient.get('/files/attachments', {
      params: {
        entity_type: entityType,
        entity_id: entityId,
      },
    }),

  updateAttachment: (attachmentId: string, payload: UpdateAttachmentRequest): Promise<StoredAttachment> =>
    apiClient.patch(`/files/attachments/${attachmentId}`, payload),

  deleteAttachment: (attachmentId: string): Promise<void> =>
    apiClient.delete(`/files/attachments/${attachmentId}`),

  getDownloadUrl: (attachmentId: string, expiresIn = 900): Promise<PresignDownloadResponse> =>
    apiClient.get(`/files/attachments/${attachmentId}/download-url`, {
      params: { expires_in: expiresIn },
    }),

  uploadToS3: async (uploadUrl: string, file: File): Promise<void> => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/octet-stream'
      },
      body: file
    })
    if (!response.ok) {
      throw new Error(`S3 upload failed with status ${response.status}`)
    }
  }
}
